// src/components/Dashboard/PlayerDashboard/MyTeamsWidget.jsx
// Shows teams the player is assigned to

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { FaUsers, FaTshirt, FaArrowRight } from 'react-icons/fa';
import styles from './MyTeamsWidget.module.css';

const MyTeamsWidget = ({ programs, selectedProgramId, userProfile, refreshTrigger }) => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch teams for all programs
  useEffect(() => {
    const fetchTeams = async () => {
      if (!programs || programs.length === 0 || !userProfile?._id) {
        setTeams([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        if (!token) {
          setLoading(false);
          return;
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch teams for each program
        const allTeams = [];
        for (const program of programs) {
          try {
            const response = await fetch(`${backendUrl}/api/teams/program/${program._id}`, { headers });
            if (response.ok) {
              const teamsData = await response.json();
              const teamsArray = Array.isArray(teamsData) ? teamsData : [teamsData];
              
              // Find teams where user is a player
              teamsArray.forEach(team => {
                const playerAssignment = (team.players || []).find(p => 
                  p.userId === userProfile._id || 
                  p.userId?._id === userProfile._id
                );
                
                if (playerAssignment) {
                  allTeams.push({
                    ...team,
                    programId: program._id,
                    programName: program.name,
                    position: playerAssignment.roleName || playerAssignment.position,
                    jerseyNumber: playerAssignment.jerseyNumber
                  });
                }
              });
            }
          } catch (error) {
            // Skip failed program fetches
          }
        }

        setTeams(allTeams);
      } catch (error) {
        console.error('Error fetching teams:', error);
        setTeams([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [programs, userProfile?._id, refreshTrigger]);

  // Filter teams by selected program
  const filteredTeams = selectedProgramId === 'all' 
    ? teams 
    : teams.filter(t => t.programId === selectedProgramId);

  const handleTeamClick = (team) => {
    navigate(`/program/${team.programId}/teams`);
  };

  const handleViewAll = () => {
    if (programs.length > 0) {
      navigate(`/program/${programs[0]._id}/teams`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <FaUsers className={styles.headerIcon} />
            <h3 className={styles.headerTitle}>My Teams</h3>
          </div>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaUsers className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>My Teams</h3>
        </div>
        {filteredTeams.length > 0 && (
          <span className={styles.count}>{filteredTeams.length}</span>
        )}
      </div>

      <div className={styles.content}>
        {filteredTeams.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>👥</span>
            <p>Not assigned to any teams</p>
            <span className={styles.emptyHint}>Team assignments will appear here</span>
          </div>
        ) : (
          <>
            <div className={styles.teamsList}>
              {filteredTeams.slice(0, 3).map((team, index) => (
                <div 
                  key={team._id || index}
                  className={styles.teamCard}
                  onClick={() => handleTeamClick(team)}
                >
                  <div className={styles.teamIcon}>
                    {team.logo ? (
                      <img src={team.logo} alt="" />
                    ) : (
                      '⚽'
                    )}
                  </div>
                  <div className={styles.teamInfo}>
                    <span className={styles.teamName}>{team.name}</span>
                    <span className={styles.programName}>{team.programName}</span>
                    <div className={styles.teamMeta}>
                      {team.position && (
                        <span className={styles.position}>
                          {team.position}
                        </span>
                      )}
                      {team.jerseyNumber && (
                        <span className={styles.jersey}>
                          <FaTshirt /> #{team.jerseyNumber}
                        </span>
                      )}
                    </div>
                  </div>
                  <FaArrowRight className={styles.teamArrow} />
                </div>
              ))}
            </div>

            {filteredTeams.length > 3 && (
              <button className={styles.viewAllBtn} onClick={handleViewAll}>
                View all {filteredTeams.length} teams <FaArrowRight />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyTeamsWidget;



